import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
  Chip,
  Grid,
  Card,
  CardMedia,
  CardActions,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image,
  Close,
  Upload
} from '@mui/icons-material';

const WarehousePhotoUploadModal = ({ open, onClose, onUpload, orderNumber, loading = false }) => {
  const { t } = useTranslation(['orders', 'common']);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      setErrorMessage(t('orders:modals.warehouseUpload.fileTooLarge', { fileName: file.name }));
      return false;
    }

    // Check file type
    const isValidType = acceptedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.replace('/*', '/'));
      }
      return file.type === type;
    });

    if (!isValidType) {
      setErrorMessage(t('orders:modals.warehouseUpload.invalidFileType', { fileName: file.name }));
      return false;
    }

    return true;
  };

  const handleFiles = (files) => {
    setErrorMessage('');
    const fileList = Array.from(files);
    
    const validFiles = fileList.filter(validateFile);
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setErrorMessage('');
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      setErrorMessage(t('orders:modals.warehouseUpload.selectAtLeastOne'));
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file, index) => {
      formData.append('images', file);
    });

    onUpload(formData);
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setErrorMessage('');
    setDragActive(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const createImagePreview = (file) => {
    return URL.createObjectURL(file);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Image color="primary" />
          <Typography variant="h6">
            {t('orders:modals.warehouseUpload.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('orders:modals.warehouseUpload.orderNumber', { orderNumber })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('orders:modals.warehouseUpload.instructions')}
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        {/* Upload Area */}
        <Paper
          elevation={dragActive ? 8 : 2}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            border: dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
            backgroundColor: dragActive ? '#E8F5E8' : 'transparent',
            transition: 'all 0.3s ease',
            mb: 3,
            '&:hover': {
              backgroundColor: '#E8F5E8',
              borderColor: '#4CAF50'
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('orders:modals.warehouseUpload.uploadArea')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('orders:modals.warehouseUpload.clickToUpload')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('orders:modals.warehouseUpload.supportedFormats')}
          </Typography>
        </Paper>

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('orders:modals.warehouseUpload.selectedFiles', { count: selectedFiles.length })}
            </Typography>
            <Grid container spacing={2}>
              {selectedFiles.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={createImagePreview(file)}
                      alt={file.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="caption" noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          {t('orders:modals.warehouseUpload.cancel')}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
          disabled={selectedFiles.length === 0 || loading}
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#45a049'
            }
          }}
        >
          {loading ? t('orders:modals.warehouseUpload.uploading') : t('orders:modals.warehouseUpload.uploadPhotos', { 
            count: selectedFiles.length,
            plural: selectedFiles.length !== 1 ? 's' : ''
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehousePhotoUploadModal;

